import React, { useState, useContext, useEffect, ReactNode } from "react";
import { Box, createStyles, makeStyles, Theme } from "@material-ui/core";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import CustomDialog, { CustomDialogProps } from "../../../components/Dialog/CustomDialog";
import CustomButton from "../../../components/Button/CustomButton";
import { ProductContext } from "../../../contexts/ProductContext";
import TestRequestApi from "../../../api/services/TestRequestApi";
import CustomRadioGroup from "../../../components/Input/CustomRadioGroup";
import CustomTextField from "../../../components/Input/CustomTextField";
import CustomDatePicker from "../../../components/Input/CustomDatePicker";
import CustomDropdown from "../../../components/Input/CustomDropdown";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import useEnv from "../../../hooks/env";
import { SampleType, WithPredefinedChip } from "../../../components/Chip/PredefinedSampleChip";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import { parseDate } from "../../../api/services/common/FormatDateService";
import InfoBox from "../../../components/InfoBox";
import GalleryUploadButton from "../../../components/Gallery/button/GalleryUploadButton";
import GalleryButton from "../../../components/Gallery/button/GalleryButton";
import HiddenError from "../../../models/HiddenError";
import ProductSampleGallery from "./ProductSampleGallery";
import ProductAttachment from "../../../api/models/product/ProductAttachment";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import Attachment from "../../../api/models/product/Attachment";
import ProductSamplePreviewGallery from "./ProductSamplePreviewGallery";
import ProductSide, { PRODUCT_SIDES, PRODUCT_SIDE_DICTIONARY } from "../models/ProductSide.model";
import { CompetitorProductSample } from "../../../api/models/product/ProductSampleReference";
import ProductSamplesApi from "../../../api/services/ProductSamplesApi";
import CompetitorProduct from "../../../api/models/product/CompetitorProduct";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../../functions/confidentialCheck.function";
import ConfidentialConfirmationDialog from "../../../components/Dialog/ConfidentialConfirmationDialog";

type Deliverer = TestRequestRaw.Schemas.Deliverer;

interface ProductSampleDetailsCompetitorDialogProps extends CustomDialogProps {
  disabled?: boolean;
  testRequestID: string;
  competitorProperties?: CompetitorProduct | null;
  callback: (closeDialog: boolean) => void;
}

interface ProductSampleCompetitorDetailsForm {
  company: string;
  deliveredBy: Deliverer;
  descriptionOrNotes: string;
  id: number;
  lotNumber: string;
  manufacturingDate: Date | null;
  productSampleName: string;
  productName: string;
  productVariant: string;
  receivedOn: Date | null;
  sideOfProduct: ProductSide;
  source: string;
  attachments?: TestRequestRaw.Schemas.ProductSampleAttachmentReferenceDTO[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      marginBottom: theme.spacing(5),
    },
  }),
);

const ProductSampleDetailsCompetitorDialog = (props: ProductSampleDetailsCompetitorDialogProps) => {
  const { competitorProperties, callback, disabled, open, onClose, testRequestID } = props;
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const productSampleApi = new ProductSamplesApi(api);
  const { productObject, competitorCompany, competitorProduct, updateProductObject } = useContext(ProductContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const classes = useStyles(props);
  const [sampleName, setSampleName] = useState("");
  const [showConfidentialDialog, setShowConfidentialDialog] = useState(false);
  const [pendingValues, setPendingValues] = useState<ProductSampleCompetitorDetailsForm | null>(null);
  const [confidentialRegex, setConfidentialRegex] = useState<RegExp | null>(null);
  const { productSamples, newProductSample } = React.useContext(TestRequestPermissionContext);
  let formRef: FormikProps<ProductSampleCompetitorDetailsForm> | null = null;
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const isPredefined = !!productObject?.usedIn;
  const permissions = productObject ? productSamples?.[productObject.id] : newProductSample;
  const formFieldsDisabled = disabled || !permissions?.editDefinition;

  useEffect(() => {
    fetchHighlyConfidentialRegex().then(setConfidentialRegex);
  }, []);

  const yupValidationSchema = Yup.object().shape({
    company: Yup.string(),
    deliveredBy: Yup.string().required(),
    descriptionOrNotes: Yup.string(),
    id: Yup.number(),
    lotNumber: Yup.string(),
    manufacturingDate: Yup.string().nullable(),
    productSampleName: Yup.string()
      .required("Please enter a sample name")
      .test(
        "invalid character test",
        "Name cannot contain a hash '#' symbol",
        (value) => value && !value.includes("#"),
      ),
    productName: Yup.string(),
    productVariant: Yup.string(),
    receivedOn: Yup.string().nullable(),
    sideOfProduct: Yup.string().required(),
    source: Yup.string(),
  });

  useEffect(() => {
    setSampleName(
      productObject?.productSampleName ||
        (competitorCompany && competitorProduct ? `${competitorCompany.name} ${competitorProduct.name}` : ""),
    );
  }, [productObject, competitorCompany, competitorProduct]);

  const showGallery = open && (productObject || productObject?.id === 0) && testRequestID && productObject?.attachments;
  const showPreviewGallery = open && !showGallery && !!competitorProduct?.attachments?.length;
  const showExtraSaveButton = !disabled && !showGallery && !productObject?.id && permissions?.editAttachments;
  const showGalleryAddButton = !disabled && showGallery && permissions?.editAttachments;

  const save = (values: ProductSampleCompetitorDetailsForm): Promise<CompetitorProductSample> => {
    const payload: CompetitorProductSample = {
      attachments: values?.attachments as TestRequestRaw.Schemas.ProductSampleAttachmentReferenceDTO[],
      id: productObject?.id,
      competitorCompanyId: competitorCompany.id,
      competitorCompanyName: values.company,
      competitorProductId: competitorProduct?.id,
      competitorProductName: competitorProduct?.name,
      deliveredBy: values.deliveredBy,
      descriptionOrNotes: values.descriptionOrNotes,
      lotNumber: values.lotNumber,
      manufacturingDate: values.manufacturingDate?.toISOString() as string,
      productVariant: values.productVariant,
      receivedOn: values.receivedOn?.toISOString() as string,
      productSampleName: values.productSampleName,
      sideOfProduct: values.sideOfProduct,
      source: values.source,
      type: "COMPETITOR_PRODUCT" as "COMPETITOR_PRODUCT",
    };

    if (productObject) {
      return productSampleApi.updateProductSample(testRequestID, payload).then((res) => {
        showSnackbar("Product sample updated", "success");
        return res.data as CompetitorProductSample;
      });
    }

    return testRequestApi
      .createCompetitorProductSample({
        ...payload,
        initialAttachments: competitorProduct?.attachments.map((a: Attachment) => a.attachmentReference),
      })
      .then((res) => {
        if (permissions?.editDefinition && testRequestID) {
          const newProductSampleId = Number(res.data.id);
          return productSampleApi.updateTestRequestProducts(testRequestID, [newProductSampleId]).then(() => {
            showSnackbar("Product sample created", "success");
            onClose();
            return {
              ...res.data,
            };
          });
        }
        return res.data;
      });
  };

  const update = (
    values: ProductSampleCompetitorDetailsForm,
    actions: FormikHelpers<ProductSampleCompetitorDetailsForm>,
  ): void => {
    if (disabled || !formRef?.dirty || !permissions?.editDefinition) {
      actions.resetForm();
      callback(true);
      onClose();
    } else {
      const fieldsToCheck = [values.productSampleName, values.source, values.descriptionOrNotes, values.lotNumber];

      if (confidentialRegex && containsHighlyConfidentialInfo(fieldsToCheck, confidentialRegex)) {
        setPendingValues(values);
        setShowConfidentialDialog(true);
        return;
      }

      save(values)
        .then(() => {
          actions.resetForm();
          callback(true);
          onClose();
        })
        .catch(handleHttpError);
    }
  };

  return (
    <>
      <ProductContext.Consumer>
        {() => {
          return (
            <CustomDialog fullWidth maxWidth="lg" onClose={onClose} open={open}>
              <h2 className={classes.header}>
                {isPredefined ? (
                  <WithPredefinedChip
                    details={{
                      testRequestId: productObject.usedIn,
                      sampleType: SampleType.Product,
                    }}
                    spacing={2}
                    text={`COMPETITOR PRODUCT ${sampleName}`}
                  />
                ) : (
                  <>COMPETITOR PRODUCT {sampleName}</>
                )}
              </h2>
              <h3 className={classes.header}>Product Sample Definition</h3>
              <Formik
                innerRef={(ref) => {
                  if (ref) {
                    formRef = ref;
                  }
                }}
                initialValues={{
                  id: productObject?.id || "",
                  company: competitorCompany.name || competitorProperties?.company.name || "",
                  deliveredBy: productObject?.deliveredBy || "",
                  descriptionOrNotes: productObject?.descriptionOrNotes || "",
                  lotNumber: productObject?.lotNumber || "",
                  manufacturingDate: parseDate(productObject?.manufacturingDate),
                  productSampleName: sampleName,
                  productName: competitorProduct?.name || competitorProperties?.name || "",
                  productVariant: productObject?.productVariant || "",
                  receivedOn: parseDate(productObject?.receivedOn),
                  sideOfProduct: productObject?.sideOfProduct || "OPEN",
                  source: productObject?.source || "",
                }}
                onSubmit={update}
                validationSchema={yupValidationSchema}
              >
                {(formikProps: FormikProps<ProductSampleCompetitorDetailsForm>): ReactNode => {
                  const { errors, isSubmitting, handleBlur, handleChange, setFieldValue, touched, values } =
                    formikProps;
                  return (
                    <Form>
                      <Box pb={4}>
                        <Grid container justify="flex-start" direction="row" spacing={4}>
                          <Grid item lg={6} md={6} sm={6} xs={6}>
                            <InfoBox text="Keep the sample name as short as possible.">
                              <CustomTextField
                                autoFocus
                                fullWidth
                                mandatory
                                disabled={formFieldsDisabled}
                                name="productSampleName"
                                id="productSampleName"
                                label="Sample Name"
                                value={values.productSampleName}
                                type="productSampleName"
                                helperText={[
                                  errors.productSampleName && touched.productSampleName ? errors.productSampleName : "",
                                ].join(" ")}
                                error={!!(errors.productSampleName && touched.productSampleName)}
                                onChange={(e) => {
                                  setSampleName(e.target.value);
                                  handleChange(e);
                                }}
                                onBlur={handleBlur}
                              />
                            </InfoBox>
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled
                              name="company"
                              id="company"
                              label="Company"
                              value={values.company}
                              type="company"
                              helperText={[errors.company && touched.company ? errors.company : ""].join(" ")}
                              error={!!(errors.company && touched.company)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            {productObject && (
                              <CustomTextField
                                fullWidth
                                disabled
                                name="id"
                                id="id"
                                label="Product Sample ID"
                                value={values.id}
                                type="id"
                                helperText={[errors.id && touched.id ? errors.id : ""].join(" ")}
                                error={!!(errors.id && touched.id)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            )}
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomDatePicker
                              disabled={formFieldsDisabled}
                              label="Received On"
                              value={values.receivedOn}
                              onChange={(date) => setFieldValue("receivedOn", date)}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <InfoBox text="This can be the Customer, Requester or Plant.">
                              <CustomTextField
                                fullWidth
                                disabled={formFieldsDisabled}
                                name="source"
                                id="source"
                                label="Source"
                                value={values.source}
                                type="source"
                                helperText={[errors.source && touched.source ? errors.source : ""].join(" ")}
                                error={!!(errors.source && touched.source)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </InfoBox>
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled
                              mandatory
                              name="productName"
                              id="productName"
                              label="Product Name"
                              value={values.productName}
                              type="productName"
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled={formFieldsDisabled}
                              name="productVariant"
                              id="productVariant"
                              label="Product Variant"
                              value={values.productVariant}
                              type="productVariant"
                              helperText={[
                                errors.productVariant && touched.productVariant ? errors.productVariant : "",
                              ].join(" ")}
                              error={!!(errors.productVariant && touched.productVariant)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <InfoBox text="Indicate the side of the tape you want to test. If you want to test both sides of a tape, please create two product samples. One per side!">
                              <CustomDropdown
                                mandatory
                                disabled={formFieldsDisabled}
                                labelText="Side of the Product"
                                name="sideOfProduct"
                                id="sideOfProduct"
                                value={values.sideOfProduct}
                                type="sideOfProduct"
                                error={!!(errors.sideOfProduct && touched.sideOfProduct)}
                                onChange={handleChange}
                              >
                                {PRODUCT_SIDES.map((ps) => (
                                  <MenuItem value={ps}>{PRODUCT_SIDE_DICTIONARY[ps] || ""}</MenuItem>
                                ))}
                              </CustomDropdown>
                            </InfoBox>
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomRadioGroup
                              disabled={formFieldsDisabled}
                              error={!!(errors.deliveredBy && touched.deliveredBy)}
                              formLabel="Delivered By"
                              mandatory
                              name="deliveredBy"
                              options={["CSC Lab", "Requester"]}
                              onChange={handleChange}
                              value={values.deliveredBy}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomDatePicker
                              disabled={formFieldsDisabled}
                              helperText="When the roll was produced"
                              label="Manufacturing Date"
                              value={values.manufacturingDate}
                              onChange={(date) => setFieldValue("manufacturingDate", date)}
                            />
                          </Grid>
                          <Grid item lg={3} md={3} sm={3} xs={3}>
                            <CustomTextField
                              fullWidth
                              disabled={formFieldsDisabled}
                              name="lotNumber"
                              id="lotNumber"
                              label="Lot Number"
                              value={values.lotNumber}
                              type="lotNumber"
                              helperText={[errors.lotNumber && touched.lotNumber ? errors.lotNumber : ""].join(" ")}
                              error={!!(errors.lotNumber && touched.lotNumber)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <CustomTextField
                              fullWidth
                              disabled={formFieldsDisabled}
                              name="descriptionOrNotes"
                              id="descriptionOrNotes"
                              label="Description or Notes"
                              value={values.descriptionOrNotes}
                              type="descriptionOrNotes"
                              helperText={[
                                errors.descriptionOrNotes && touched.descriptionOrNotes
                                  ? errors.descriptionOrNotes
                                  : "",
                              ].join(" ")}
                              error={!!(errors.descriptionOrNotes && touched.descriptionOrNotes)}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      <Box display="flex" flexDirection="row">
                        {showGalleryAddButton && (
                          <Box marginRight={2}>
                            <GalleryUploadButton hideIfNonEmpty label="Add Attachment" />
                          </Box>
                        )}
                        {showExtraSaveButton && (
                          <GalleryButton
                            icon="ADD"
                            text="Add Attachment"
                            onClick={() =>
                              showConfirmationDialog({
                                headline: "Save Product Sample",
                                text: "You have to save the Product Sample to add attachments.",
                                confirmText: "Save",
                              })
                                .then(() => {
                                  return formRef?.validateForm();
                                })
                                .then((formErrors) => {
                                  if (formErrors && Object.keys(formErrors).length > 0) {
                                    formRef?.submitForm();
                                    throw new HiddenError("form validation failed");
                                  }
                                  return formRef?.values;
                                })
                                .then((formValues) => {
                                  if (formValues) {
                                    return save(formValues).then((response) => {
                                      callback(false);
                                      formRef?.setFieldValue("id", response.id || null);
                                      updateProductObject(response);
                                    });
                                  }
                                })
                                .catch(handleHttpError)
                            }
                          />
                        )}
                      </Box>
                      {showGallery && (
                        <ProductSampleGallery
                          editable={permissions?.editAttachments || false}
                          testRequestId={testRequestID}
                          productSampleId={productObject?.id}
                          productSampleName={sampleName}
                          productSampleAttachments={productObject?.attachments as ProductAttachment[]}
                          onChange={(attachments) => {
                            const existingAttachments = productObject?.attachments;
                            if (existingAttachments) {
                              existingAttachments.splice(0, existingAttachments.length, ...attachments);
                            }
                          }}
                        />
                      )}
                      {showPreviewGallery && competitorProduct && (
                        <ProductSamplePreviewGallery
                          companyName={competitorProduct.company?.name || ""}
                          productId={competitorProduct.id}
                          productName={competitorProduct.name}
                          productAttachments={competitorProduct.attachments || []}
                        />
                      )}
                      <Grid container direction="row" justify="flex-end">
                        <CustomButton
                          disabled={isSubmitting}
                          primary
                          text={permissions?.editDefinition ? "Save and Close" : "Close"}
                          type="submit"
                        />
                      </Grid>
                    </Form>
                  );
                }}
              </Formik>
            </CustomDialog>
          );
        }}
      </ProductContext.Consumer>
      {/* Confidential confirmation dialog */}
      {showConfidentialDialog && (
        <ConfidentialConfirmationDialog
          objectName="competitor product sample"
          open={showConfidentialDialog}
          onCancel={() => {
            setShowConfidentialDialog(false);
            setPendingValues(null);
          }}
          onProceed={() => {
            if (pendingValues && testRequestID) {
              testRequestApi
                .updateConfidentiality(testRequestID, "HIGHLY_CONFIDENTIAL")
                .then(() => {
                  showSnackbar("Test request set to highly confidential", "success");
                  return save(pendingValues);
                })
                .then(() => {
                  formRef?.resetForm();
                  callback(true);
                  onClose();
                  showSnackbar("Competitor product sample updated", "success");
                })
                .catch(handleHttpError);
            }
            setShowConfidentialDialog(false);
            setPendingValues(null);
          }}
        />
      )}
    </>
  );
};
export default ProductSampleDetailsCompetitorDialog;
